import { createTheme } from "@mui/material/styles";

const theme = createTheme({
  palette: {
    mode: "dark",
    primary: {
      main: "#00ADB5", // Teal; refine this
      light: "#22d3ee", // Example lighter shade
      dark: "#00838f", // Example darker shade
    },
    secondary: {
      main: "#1e1e1e", // Dark gray/black; refine!
      light: "#2c2c2c", // Slightly lighter
      dark: "#121212", // Very dark
    },
    background: {
      default: "#101010",
      paper: "#000000", // For cards, dialogs, etc.
      black_shade_secondary: "#1e1e1e", // For cards, dialogs, etc.
    },
    text: {
      primary: "#ffffff",
      secondary: "#e0e0e0",
      dark: "#000000",
    },
    analyticCards: {
      1: "#DCD8FB",
      2: "#FFE6CC",
      3: "#F3E5F5",
      4: "#DFF5E3",
    },
    memberColors: {
      BLUEWHALE: "#1976d2",
      BELUGA: "#ea00ff",
      WHALE: "#8c00ff",
      TRUSTED: "#af58f2",
      MEMBER: "#4caf50",
      NOROLE: "#333333",
    },
    memberColorsBG: {
      BLUEWHALE: "rgba(25, 118, 210, 0.2)",
      BELUGA: "rgb(234, 0, 255, 0.2)",
      WHALE: "rgb(140, 0, 255, 0.2)",
      TRUSTED: "rgb(175, 88, 242, 0.2)",
      MEMBER: "rgb(76, 175, 80, 0.2)",
      NOROLE: "rgb(51, 51, 51, 0.2)",
    },
    error: {
      main: "#f44336",
    },
    warning: {
      main: "#ff9800",
    },
    info: {
      main: "#2196f3",
    },
    success: {
      main: "#4caf50",
    },
  },
  typography: {
    fontFamily: "Inter, Roboto, sans-serif", // Verify; could be another sans-serif
    h1: {
      fontSize: "32px", // Adjust sizes as needed
      fontWeight: "bold",
      color: "#ffffff",
    },
    h2: {
      /* ... */
    }, // Define other heading levels
    body1: {
      fontSize: "1rem",
    },
    body2: {
      fontSize: "0.875rem",
      color: "#e0e0e0", // For lighter text
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          borderRadius: "10px",
          fontWeight: "bold",
          fontSize: "16px",
          padding: "8px 16px",
          margin: "16px 0px ",
        },
        text: {
          // Styles for text buttons
          color: "#ffffff !important",
          "&:hover": {
            backgroundColor: "rgba(0, 173, 181, 0.1)", // Light tint on hover
          },
          "&.Mui-disabled": {
            // Styles for disabled buttons
            backgroundColor: "none !important",
            color: "rgba(255, 255, 255, 0.3) !important",
            "&:hover": {
              backgroundColor: "#e0e0e0 !important", // Ensure no hover effect
            },
          },
        },
        outlined: {
          // Styles for outlined buttons
          borderColor: "#ffffff !important", // Primary border color
          color: "#ffffff !important",
          "&:hover": {
            backgroundColor: "rgba(0, 173, 181, 0.1)", // Light tint on hover
            borderColor: "#00838f", // Darker border on hover
          },
          "&.Mui-disabled": {
            // Styles for disabled buttons
            border: "2px solid rgba(255, 255, 255, 0.3) !important",
            color: "rgba(255, 255, 255, 0.3) !important",
            "&:hover": {
              backgroundColor: "#e0e0e0 !important", // Ensure no hover effect
            },
          },
        },
        contained: {
          // Styles for contained buttons
          backgroundColor: "#ffffff", // Primary background color
          color: "#000 !important",
          "&:hover": {
            backgroundColor: "#00838f", // Darker background on hover
          },
          "&.Mui-disabled": {
            // Styles for disabled buttons
            backgroundColor: "rgba(255, 255, 255, 0.3) !important",
            color: "#ffffff !important",
            "&:hover": {
              backgroundColor: "#e0e0e0 !important", // Ensure no hover effect
            },
          },
        },
        success: {
          backgroundColor: "#4CAF50 !important", // Green background
          color: "#ffffff !important",
          "&:hover": {
            backgroundColor: "#45a049 !important", // Darker green on hover
          },
          "&.Mui-disabled": {
            backgroundColor: "rgba(76, 175, 80, 0.3) !important",
            color: "#ffffff !important",
          },
        },
        // Reject variant - red buttons for denials, cancellations, etc.
        reject: {
          backgroundColor: "#FF1818 !important", // Red background
          color: "#ffffff !important",
          "&:hover": {
            backgroundColor: "#d32f2f !important", // Darker red on hover
          },
          "&.Mui-disabled": {
            backgroundColor: "rgba(244, 67, 54, 0.3) !important",
            color: "#ffffff !important",
          },
        },
        "&.Mui-disabled": {
          // Styles for disabled buttons
          backgroundColor: "rgba(255, 255, 255, 0.3) !important",
          color: "#ffffff !important",
          "&:hover": {
            backgroundColor: "#e0e0e0 !important", // Ensure no hover effect
          },
        },
      },
      defaultProps: {
        disableRipple: false, // Optional: Disable ripple effect globally for buttons
      },
    },

    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiInputBase-root": {
            borderRadius: "10px",
            backgroundColor: "none",
            border: "2px solid rgba(255,255,255,0.6) !important",
            fontFamily: "Inter",
            transition: "border-color 0.3s",
            "&:hover": {
              borderRadius: "10px",
              border: "2px solid #ffffff !important",
            },
            "&.Mui-focused": {
              borderRadius: "10px",
              border: "2px solid #ffffff !important",
            },
          },
          "& .MuiInputBase-input": {
            color: "white",
          },
          "& .MuiInputLabel-root": {
            color: "white",
            "&.Mui-focused": {
              color: "white",
            },
          },
          "& .MuiSvgIcon-root": {
            fill: "#ffffff !important",
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          "& .MuiInputBase-root": {
            borderRadius: "10px",
            backgroundColor: "none",
            border: "2px solid rgba(255,255,255,0.6) !important",
            fontFamily: "Inter",
            transition: "border-color 0.3s",
            "&:hover": {
              borderRadius: "10px",
              border: "2px solid #ffffff !important",
            },
            "&.Mui-focused": {
              borderRadius: "10px",
              border: "2px solid #ffffff !important",
            },
          },
          "& .MuiInputBase-input": {
            color: "white",
            borderRadius: "10px",
            border: "2px solid #ffffff !important",
          },
          "& .MuiInputLabel-root": {
            color: "white",
            "&.Mui-focused": {
              color: "white",
            },
          },
          "& .MuiSvgIcon-root": {
            fill: "#fff !important",
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-notchedOutline": {
            border: "none", // Default outline
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            border: "none", // Outline on hover
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            border: "none", // Outline on focus
          },
        },
      },
    },

    MuiMenuItem: {
      styleOverrides: {
        root: {
          color: "white", // Default text color for menu items
          "&.Mui-selected": {
            backgroundColor: "rgba(0, 173, 181, 0.1)", // Background color when selected
          },
          "&.Mui-selected:hover": {
            backgroundColor: "rgba(0, 173, 181, 0.2)", // Background color on hover when selected
          },
        },
      },
    },
    MuiTable: {
      styleOverrides: {
        root: {
          backgroundColor: "#101010", // Background color for the table
          color: "white",
          borderRadius: "10px",
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          "&:hover": {
            backgroundColor: "rgba(0, 173, 181, 0.1)", // Hover effect for rows
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          color: "white", // Text color for cells
          borderBottom: "1px solid #1e1e1e", // Cell border color
        },
        head: {
          backgroundColor: "#1e1e1e",
          fontFamily: "Inter SemiBold",
          letterSpacing: "0.5px",
          fontWeight: "500",
        },
        body: {
          fontSize: "0.875rem", // Font size for body cells
        },
      },
    },

    MuiDialog: {
      styleOverrides: {
        root: {
          "& .MuiBackdrop-root": {
            backgroundColor: "rgba(255, 255, 255, 0.2)", // Change this to your desired color
          },
        },
        paper: {
          "--Paper-overlay": "none !important",
          borderRadius: "10px",
        },
      },
    },

    MuiCheckbox: {
      styleOverrides: {
        root: {
          borderColor: "#101010",
          backgroundColor: "transparent",
          "&:hover": {
            backgroundColor: "rgba(255, 255, 255, 0.08)",
          },
          "&.MuiCheckbox-indeterminate": {
            backgroundColor: "#ffffff",
            color: "#000000",
          },
          borderRadius: "4px",
          borderWidth: "none",
          borderStyle: "solid",
        },
      },
    },

    MuiAccordion: {
      styleOverrides: {
        root: {
          backgroundColor: "#101010",
          color: "#ffffff",
          borderRadius: "10px !important",
          "&:before": {
            display: "none",
          },
          "&.Mui-expanded": {
            borderRadius: "10px",
          },
          "&:first-of-type": {
            borderRadius: "10px",
          },
          "&:last-of-type": {
            borderRadius: "10px",
          },
        },
      },
    },
    MuiToggleButtonGroup: {
      styleOverrides: {
        root: {
          backgroundColor: "transparent",
          "& .MuiToggleButtonGroup-grouped": {
            margin: 0.5,
            border: "1px solid",
            borderColor: "#ffffff40",
            "&.Mui-disabled": {
              border: "1px solid rgba(255, 255, 255, 0.12)",
            },
          },
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          borderRadius: "10px",
          textTransform: "none",
          fontWeight: "medium",
          color: "#ffffff",
          borderColor: "#ffffff40",
          "&:hover": {
            backgroundColor: "rgba(255, 255, 255, 0.08)",
          },
          "&.Mui-selected": {
            backgroundColor: "#00838f !important",
            color: "white !important",
            "&:hover": {
              backgroundColor: "rgba(0, 173, 181, 0.2) !important",
            },
          },
          "&.Mui-disabled": {
            color: "rgba(255, 255, 255, 0.3)",
          },
        },
      },
    },

    // Additive alignment toward the member-side shadcn/ui design system
    // (14px card/dialog radius, 9999px pill chips, teal-tinted hover states)
    // for components not already covered above. Presentational only.
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: "none",
        },
        rounded: {
          borderRadius: "14px",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: "14px",
          border: "1px solid rgba(255,255,255,0.06)",
          backgroundColor: "#161618",
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: "9999px",
          fontWeight: 700,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: "#1e1e1e",
          border: "1px solid rgba(255,255,255,0.08)",
          fontSize: "12px",
          borderRadius: "8px",
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: "#c3c8cc",
          "&:hover": {
            backgroundColor: "rgba(0, 173, 181, 0.1)",
            color: "#ffffff",
          },
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        root: {
          color: "#c3c8cc",
        },
        selectIcon: {
          color: "#c3c8cc",
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          border: "2px solid rgba(255,255,255,0.1)",
        },
      },
    },
  },
});

export default theme;
